import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Activity, Megaphone, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EarthquakeMap from "@/components/EarthquakeMap";
import { toast } from "sonner";

function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function SeverityBadge({ s }) {
  const cls =
    s === "emergency" ? "bg-[color:var(--emergency)] text-white" :
    s === "warning" ? "bg-[color:var(--coral)] text-white" :
    "bg-[color:var(--navy)]/10 text-[color:var(--navy)]";
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{s}</span>;
}

function AdminAlertForm({ onPosted }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("info");
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  if (!user || user.role !== "admin") return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/alerts", { title, body, severity });
      setTitle(""); setBody(""); setSeverity("info");
      onPosted?.();
    } finally { setBusy(false); }
  };
  const sendTest = async () => {
    queueMicrotask(() => setTestBusy(true));
    try {
      const { data } = await http.post("/push/test");
      if (data.sent > 0) {
        toast.success(`Test push delivered to ${data.sent} device${data.sent === 1 ? "" : "s"}`);
      } else if (data.failed > 0) {
        toast.error(`Test push failed on ${data.failed} device${data.failed === 1 ? "" : "s"}${data.pruned ? ` · pruned ${data.pruned} stale` : ""}`);
      } else {
        toast("No devices subscribed yet. Click the bell in the header on the device you want to receive alerts on.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push");
    } finally {
      queueMicrotask(() => setTestBusy(false));
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-alert-form" className="clay p-5 space-y-3">
      <p className="eyebrow text-[color:var(--coral)]">{t("admin_label")} · {t("admin_post")}</p>
      <Input data-testid="admin-alert-title" placeholder={t("alert_title")} value={title} onChange={(e) => setTitle(e.target.value)} required />
      <Textarea data-testid="admin-alert-body" placeholder={t("alert_body")} value={body} onChange={(e) => setBody(e.target.value)} required rows={3} />
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger data-testid="admin-alert-severity" className="w-[160px] rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="info">{t("info")}</SelectItem>
            <SelectItem value="warning">{t("warning")}</SelectItem>
            <SelectItem value="emergency">{t("emergency")}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          onClick={sendTest}
          disabled={testBusy}
          data-testid="admin-push-test"
          variant="outline"
          className="pill border-[color:var(--coral)]/40 text-[color:var(--coral)] hover:bg-[color:var(--coral)]/10"
          title="Send a sample push notification to your subscribed devices"
        >
          {testBusy ? "Sending…" : "Send test push"}
        </Button>
        <Button data-testid="admin-alert-submit" type="submit" disabled={busy} className="pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)] ml-auto">
          {busy ? "…" : t("admin_post")}
        </Button>
      </div>
    </form>
  );
}

export default function AlertsPanel() {
  const { t } = useI18n();
  const { user } = useAuth();

  const eq = useQuery({
    queryKey: ["alerts", "earthquakes"],
    queryFn: async () =>
      (await http.get("/alerts/earthquakes", { params: { hours: 720, min_mag: 2.5, radius_miles: 350 } })).data,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const ts = useQuery({
    queryKey: ["alerts", "tsunami"],
    queryFn: async () => (await http.get("/alerts/tsunami")).data,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const adm = useQuery({
    queryKey: ["alerts", "admin"],
    queryFn: async () => (await http.get("/alerts")).data,
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const deleteAlert = async (id) => {
    if (!window.confirm(t("delete_alert_confirm"))) return;
    await http.delete(`/alerts/${id}`);
    adm.refetch();
  };

  const tsunamiActive = ts.data?.active_warning;
  const earthquakes = eq.data?.items || [];
  const adminAlerts = adm.data || [];
  const isEmpty = !tsunamiActive && earthquakes.length === 0 && adminAlerts.length === 0;

  return (
    <section id="alerts" data-testid="alerts-panel" className="clay p-6 md:p-7 scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="eyebrow">{t("alerts")}</p>
          <h3 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>{t("alerts")}</h3>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center">
          <AlertTriangle className="w-6 h-6" />
        </div>
      </div>

      {/* Tsunami */}
      {tsunamiActive && (
        <div data-testid="tsunami-active" className="rounded-2xl bg-[color:var(--emergency)] text-white p-4 mb-3 pulse-emergency">
          <p className="eyebrow text-white/90">{t("tsunami")} · ACTIVE</p>
          <p className="font-semibold mt-1">{ts.data?.items?.[0]?.title}</p>
        </div>
      )}

      {/* Admin alerts */}
      {adminAlerts.length > 0 && (
        <div className="mb-3 space-y-2">
          {adminAlerts.slice(0, 5).map((a) => (
            <div key={a.id} data-testid={`admin-alert-${a.id}`} className="rounded-2xl bg-[color:var(--sand-2)] p-4 flex items-start gap-3">
              <Megaphone className="w-4 h-4 mt-0.5 text-[color:var(--navy)]" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <SeverityBadge s={a.severity || "info"} />
                  <span className="text-xs text-[color:var(--ink-2)]">{fmtAgo(a.created_at)}</span>
                </div>
                <p className="font-semibold">{a.title}</p>
                <p className="text-sm text-[color:var(--ink-2)] mt-0.5">{a.body}</p>
              </div>
              {user?.role === "admin" && (
                <Button data-testid={`delete-alert-${a.id}`} variant="ghost" size="icon" onClick={() => deleteAlert(a.id)}>
                  <Trash2 className="w-4 h-4 text-[color:var(--ink-2)]" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Earthquakes */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-2 gap-2">
          <p className="eyebrow flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" />
            {t("earthquakes")} · 30d · {Math.round(eq.data?.radius_miles || 350)} mi · M2.5+
          </p>
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
            <span className={`w-1.5 h-1.5 rounded-full ${eq.isFetching ? "bg-[color:var(--coral)]" : "bg-[color:var(--teal)]"} animate-pulse`} />
            {eq.isFetching ? t("updating_label") : t("live_label")}
          </span>
        </div>

        {/* Live epicenter map */}
        <div className="mb-2.5">
          <EarthquakeMap quakes={earthquakes} radiusMiles={eq.data?.radius_miles || 350} />
        </div>

        {earthquakes.length === 0 ? (
          <p data-testid="no-quakes" className="text-xs text-[color:var(--ink-2)] italic text-center py-2">
            {t("no_quakes_msg")}
          </p>
        ) : (
          <div className="space-y-1 max-h-44 overflow-y-auto thin-scroll">
            {earthquakes.slice(0, 20).map((q) => (
              <a
                key={q.id}
                href={q.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`quake-${q.id}`}
                className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-[color:var(--sand-2)] transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-xl font-bold ${
                      q.magnitude >= 5 ? "bg-[color:var(--emergency)] text-white" :
                      q.magnitude >= 4 ? "bg-[color:var(--coral)] text-white" :
                      "bg-[color:var(--navy)]/10 text-[color:var(--navy)]"
                    }`}
                  >{q.magnitude?.toFixed(1)}</span>
                  <span className="text-sm">
                    <span className="block font-medium">{q.place}</span>
                    <span className="text-xs text-[color:var(--ink-2)]">
                      {fmtAgo(q.time)} · {q.depth_km?.toFixed(0)} km
                      {q.distance_miles != null && <> · {q.distance_miles} mi {t("from_popoyo")}</>}
                    </span>
                  </span>
                </span>
                {q.tsunami && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--emergency)] text-white">tsunami</span>
                )}
              </a>
            ))}
          </div>
        )}
      </div>

      {isEmpty && (
        <p className="text-sm text-[color:var(--ink-2)]">{t("no_alerts")}</p>
      )}

      <div className="mt-4">
        <AdminAlertForm onPosted={() => adm.refetch()} />
      </div>
    </section>
  );
}

// Export helper for header badge
export function useAlertCount() {
  const eq = useQuery({
    queryKey: ["alerts", "earthquakes"],
    queryFn: async () =>
      (await http.get("/alerts/earthquakes", { params: { hours: 720, min_mag: 2.5, radius_miles: 350 } })).data,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });
  const ts = useQuery({
    queryKey: ["alerts", "tsunami"],
    queryFn: async () => (await http.get("/alerts/tsunami")).data,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
  const adm = useQuery({
    queryKey: ["alerts", "admin"],
    queryFn: async () => (await http.get("/alerts")).data,
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });
  let count = 0;
  if (ts.data?.active_warning) count += 1;
  count += (eq.data?.items || []).length;
  count += (adm.data || []).length;
  return count;
}
